import { useState } from 'react';
import { LockIcon, TriangleAlertIcon } from 'lucide-react';

import { Button } from '@/components/ui/button';
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxInputGroup,
  ComboboxItem,
  ComboboxList,
} from '@/components/ui/combobox';
import { useWorkspaceList } from '@/features/workspaces';
import type {
  NotificationChannel,
  NotificationGroup,
  PreferenceCell,
  QuietHours,
} from '@/lib/api/generated/model';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { cn } from '@/lib/utils';
import { SettingsPanel } from '@/shared/components/SettingsPanel';
import { usePhoneWidth } from '@/shared/hooks/usePhoneWidth';

import {
  SETTINGS_CHANNELS as CHANNELS,
  SETTINGS_COPY,
  SETTINGS_GROUPS as GROUPS,
} from '../copy';
import { usePreferences } from '../hooks/usePreferences';

function Switch({
  checked,
  disabled,
  label,
  onChange,
}: {
  checked: boolean;
  disabled?: boolean;
  label: string;
  onChange: (next: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => {
        onChange(!checked);
      }}
      className={cn(
        'focus-visible:ring-ring touch-hit relative inline-flex h-5 w-9 shrink-0 items-center rounded-full transition-colors focus-visible:ring-3 focus-visible:outline-none disabled:cursor-not-allowed disabled:opacity-60',
        checked ? 'bg-primary' : 'bg-muted-foreground/30',
      )}
    >
      <span
        aria-hidden="true"
        className={cn(
          'bg-background size-4 rounded-full shadow-rest transition-transform',
          checked ? 'translate-x-4.5' : 'translate-x-0.5',
        )}
      />
    </button>
  );
}

/**
 * Notification settings (spec 133), as a page of their own (spec 142): which
 * groups reach you in the inbox, by email (spec 134) and as alerts (spec 136),
 * quiet hours, and which workspaces are muted. "Needs you" in the inbox
 * cannot be turned off, so nothing that blocks work is silently lost.
 */
export function NotificationSettingsPage() {
  const prefs = usePreferences();
  const isPhone = usePhoneWidth();

  const cell = (
    group: NotificationGroup,
    channel: NotificationChannel,
  ): PreferenceCell | undefined =>
    prefs.preferences?.cells.find(
      (c) => c.group === group && c.channel === channel,
    );
  // Service spec 119: the stored zone (the browser's until one is stored) and
  // whether this server sends email at all.
  const zone =
    prefs.preferences?.timezone ??
    Intl.DateTimeFormat().resolvedOptions().timeZone;
  const emailEnabled = prefs.preferences?.email_enabled ?? true;
  const mutes = prefs.preferences?.mutes ?? [];

  // Each group's heading and its control per channel, drawn as a table row on
  // a desktop and as a card on a phone (spec 263).
  const groupRows = GROUPS.map(({ group, title, detail, noAlerts }) => {
    const required = cell(group, 'in_app')?.required ?? group === 'needs_you';
    // The email switch may carry kinds of its own (service spec
    // 154). Untouched, they do the opposite of the switch; once
    // the row is changed, they follow it.
    const email = cell(group, 'email');
    const exception = emailEnabled ? (email?.default_kinds ?? []) : [];
    const head = (
      <>
        <span className="text-foreground block text-sm font-medium">
          {title}
        </span>
        <span className="text-muted-foreground mt-0.5 block text-xs">
          {detail(zone)}
        </span>
        {required ? (
          <span className="text-muted-foreground mt-1.5 flex items-center gap-1 text-xs">
            <LockIcon
              aria-hidden="true"
              className="size-3 shrink-0"
              strokeWidth={1.75}
            />
            {SETTINGS_COPY.alwaysOn}
          </span>
        ) : null}
        {email && exception.length > 0 ? (
          // Spec 196 US6: a switch row, not a sentence with a
          // link, showing what these kinds do now. Untouched,
          // the one change it can make stores the row's own
          // value, which covers them too; after that they
          // follow the row's Email switch, and this says so.
          <span className="touch-target mt-2 flex items-center justify-between gap-3">
            <span className="flex min-w-0 flex-col">
              <span className="text-foreground text-xs font-medium">
                {SETTINGS_COPY.emailExceptionSwitch(exception)}
              </span>
              <span className="text-muted-foreground text-xs">
                {email.is_default
                  ? SETTINGS_COPY.emailExceptionApart(title, !email.enabled)
                  : SETTINGS_COPY.emailExceptionFollows}
              </span>
            </span>
            <Switch
              label={SETTINGS_COPY.emailExceptionSwitch(exception)}
              checked={email.is_default ? !email.enabled : email.enabled}
              disabled={prefs.saving || !email.is_default}
              onChange={() => {
                // Stored even though it equals the switch's
                // value: an explicit choice covers these too.
                prefs.setCell({
                  group,
                  channel: 'email',
                  enabled: email.enabled,
                });
              }}
            />
          </span>
        ) : null}
      </>
    );
    const control = (channel: NotificationChannel, says: string) => {
      if (channel === 'alert' && noAlerts) {
        return (
          <span
            role="img"
            aria-label={`${title} don't alert`}
            className="text-muted-foreground inline-block text-sm leading-5"
          >
            —
          </span>
        );
      }
      const value = cell(group, channel);
      return (
        <Switch
          label={`${title} ${says}`}
          checked={value?.enabled ?? channel === 'in_app'}
          disabled={
            prefs.saving ||
            (channel === 'in_app' && required) ||
            (channel === 'email' && !emailEnabled)
          }
          onChange={(next) => {
            prefs.setCell({
              group,
              channel,
              enabled: next,
            });
          }}
        />
      );
    };
    return { group, title, head, control };
  });

  return (
    <div className="mx-auto flex w-full max-w-[44rem] flex-col gap-6 p-4 md:p-6">
      <header className="flex flex-col gap-1">
        <h1 className="text-foreground text-xl font-semibold">
          Notification settings
        </h1>
        <p className="text-muted-foreground text-sm">
          What reaches you: in your inbox, by email, and as alerts while the app
          is open. {SETTINGS_COPY.saved}
        </p>
      </header>

      {prefs.isPending ? (
        <p className="text-muted-foreground py-6 text-center text-sm">
          Loading…
        </p>
      ) : prefs.loadFailed ? (
        <div className="flex flex-col items-center gap-2 py-6">
          <p className="text-muted-foreground text-sm">
            Your settings could not be loaded.
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void prefs.refetch()}
          >
            Try again
          </Button>
        </div>
      ) : (
        <>
          <SettingsPanel labelledBy="notif-groups">
            {isPhone ? (
              <div className="flex flex-col">
                <h2
                  id="notif-groups"
                  className="text-foreground pb-1 text-sm font-medium"
                >
                  What reaches you
                </h2>
                {/* A card per group (spec 263): its words at full width, then
                  the three switches by name, where a desktop gives the words
                  half the row beside three switch columns. */}
                <ul className="flex flex-col">
                  {groupRows.map(({ group, head, control }) => (
                    <li
                      key={group}
                      className="border-border flex flex-col gap-1 border-t py-3"
                    >
                      <div>{head}</div>
                      {CHANNELS.map(({ channel, heading, says }) => (
                        <div
                          key={channel}
                          className="flex min-h-11 items-center justify-between gap-3 text-sm"
                        >
                          <span className="text-foreground">{heading}</span>
                          {control(channel, says)}
                        </div>
                      ))}
                    </li>
                  ))}
                </ul>
              </div>
            ) : (
              <table className="w-full border-collapse">
                <caption className="sr-only">What reaches you</caption>
                <thead>
                  <tr>
                    <th
                      scope="col"
                      id="notif-groups"
                      className="text-foreground pb-3 text-left text-sm font-medium"
                    >
                      What reaches you
                    </th>
                    {CHANNELS.map(({ channel, heading }) => (
                      <th
                        key={channel}
                        scope="col"
                        className="text-muted-foreground w-14 pb-3 text-center text-xs font-medium sm:w-16"
                      >
                        {heading}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {groupRows.map(({ group, head, control }) => (
                    <tr key={group} className="border-border border-t">
                      <th
                        scope="row"
                        className="py-3 pe-3 text-left align-top font-normal"
                      >
                        {head}
                      </th>
                      {CHANNELS.map(({ channel, says }) => (
                        <td
                          key={channel}
                          className="py-3 text-center align-top"
                        >
                          {control(channel, says)}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            <div className="border-border flex flex-col gap-1.5 border-t pt-3">
              <p className="text-muted-foreground flex items-start gap-1.5 text-xs">
                <TriangleAlertIcon
                  aria-hidden="true"
                  className="text-destructive mt-px size-3.5 shrink-0"
                  strokeWidth={1.75}
                />
                Critical notifications, like a spend limit stopping work, always
                reach your inbox and alert you, even when switched off or muted.
              </p>
              <p className="text-muted-foreground text-xs">
                Alerts: pop up while the app is open.
              </p>
              {!emailEnabled ? (
                <p className="text-muted-foreground text-xs">
                  Email is not set up on this server.
                </p>
              ) : null}
            </div>
          </SettingsPanel>

          <QuietHoursPanel
            window={prefs.preferences?.quiet_hours ?? null}
            zone={zone}
            saving={prefs.saving}
            onChange={(window, onFailed) => {
              prefs.setQuietHours(window, { onError: onFailed });
            }}
          />

          <SettingsPanel labelledBy="notif-mutes">
            <div className="flex flex-col gap-0.5">
              <h2
                id="notif-mutes"
                className="text-foreground text-sm font-medium"
              >
                Muted workspaces
              </h2>
              <p className="text-muted-foreground text-xs">
                A muted workspace sends you nothing except a task waiting on
                you.
              </p>
            </div>
            <MuteList
              mutes={mutes}
              saving={prefs.saving}
              onChange={(workspaceId, muted, name) => {
                prefs.setMuted({ workspaceId, muted, name });
              }}
            />
          </SettingsPanel>
        </>
      )}
    </div>
  );
}

/**
 * What is muted, each with Unmute, and a picker to mute another (spec 142
 * US4). Mutes are the person's own, account-wide, so this page is where all
 * of them live; the workspace switcher's "Mute this workspace" is a shortcut
 * to the same state.
 *
 * Only the muted ones are listed. A switch per workspace made a wall of 51
 * off-switches for someone in many workspaces, and a switch whose "on" meant
 * silence beside a table whose "on" means "send me" read backwards.
 */
function MuteList({
  mutes,
  saving,
  onChange,
}: {
  mutes: readonly { workspace: { id: string; name?: string | null } }[];
  saving: boolean;
  onChange: (workspaceId: string, muted: boolean, name: string) => void;
}) {
  const list = useWorkspaceList({ limit: 200 });
  const [query, setQuery] = useState('');
  const muted = new Set(mutes.map((m) => m.workspace.id));
  const names = new Map(
    (list.data?.items ?? []).map((w) => [w.id, w.name] as const),
  );
  const needle = query.trim().toLowerCase();
  const offered = (list.data?.items ?? [])
    .filter((w) => !muted.has(w.id))
    .filter((w) => !needle || w.name.toLowerCase().includes(needle))
    .sort((a, b) => a.name.localeCompare(b.name));

  return (
    <div className="flex flex-col gap-3">
      {mutes.length === 0 ? (
        <p className="text-muted-foreground text-sm">{SETTINGS_COPY.noMutes}</p>
      ) : (
        <ul
          aria-label="Muted workspaces"
          className="divide-border border-border flex flex-col divide-y rounded-lg border"
        >
          {mutes.map((m) => {
            const name =
              m.workspace.name ?? names.get(m.workspace.id) ?? 'Workspace';
            return (
              <li
                key={m.workspace.id}
                className="flex items-center justify-between gap-3 py-1.5 ps-3 pe-1.5 text-sm"
              >
                <span className="text-foreground min-w-0 truncate">{name}</span>
                <Button
                  variant="ghost"
                  size="sm"
                  disabled={saving}
                  aria-label={SETTINGS_COPY.unmute(name)}
                  onClick={() => {
                    onChange(m.workspace.id, false, name);
                  }}
                >
                  Unmute
                </Button>
              </li>
            );
          })}
        </ul>
      )}
      {list.isError ? (
        <div className="flex items-center gap-2">
          <p className="text-muted-foreground text-sm">
            {SETTINGS_COPY.workspacesUnavailable}
          </p>
          <Button
            variant="outline"
            size="sm"
            onClick={() => void list.refetch()}
          >
            Try again
          </Button>
        </div>
      ) : (
        <Combobox<string, false>
          value={null}
          onValueChange={(id) => {
            if (!id) return;
            onChange(id, true, names.get(id) ?? 'The workspace');
            setQuery('');
          }}
          inputValue={query}
          onInputValueChange={setQuery}
          items={offered.map((w) => w.id)}
          itemToStringLabel={(id) => names.get(id) ?? id}
          disabled={saving || list.isPending}
        >
          <ComboboxInputGroup className="max-w-sm">
            <ComboboxInput
              placeholder={SETTINGS_COPY.mutePicker}
              aria-label={SETTINGS_COPY.mutePicker}
            />
          </ComboboxInputGroup>
          <ComboboxContent>
            <ComboboxList>
              {offered.map((w) => (
                <ComboboxItem key={w.id} value={w.id}>
                  <span className="truncate">{w.name}</span>
                </ComboboxItem>
              ))}
            </ComboboxList>
            {offered.length === 0 ? (
              <ComboboxEmpty>
                <span className="text-muted-foreground text-sm">
                  {SETTINGS_COPY.noWorkspaceMatch}
                </span>
              </ComboboxEmpty>
            ) : null}
          </ComboboxContent>
        </Combobox>
      )}
    </div>
  );
}

/** What quiet hours start as when first turned on (spec 136 US3). */
const DEFAULT_QUIET_HOURS: QuietHours = { starts: '20:00', ends: '08:00' };

/** Every half hour of the day, in the 24-hour form the sentence uses. */
const HALF_HOURS = Array.from({ length: 48 }, (_, i) => {
  const hours = String(Math.floor(i / 2)).padStart(2, '0');
  return `${hours}:${i % 2 === 0 ? '00' : '30'}`;
});

/**
 * Quiet hours (spec 136 US3): one daily window, in the person's own time
 * zone, with no alerts and with email held until it ends. One 24-hour format
 * throughout (spec 142 US3): a native time input follows the browser's locale,
 * and showed "08:00 PM" beside a sentence saying "20:00".
 */
function QuietHoursPanel({
  window,
  zone,
  saving,
  onChange,
}: {
  window: QuietHours | null;
  zone: string;
  saving: boolean;
  /** `onFailed` runs when the service refuses, so the stored window shows again. */
  onChange: (window: QuietHours | null, onFailed: () => void) => void;
}) {
  // What is being chosen, which may not be saveable yet; the stored window
  // is the fallback whenever nothing is being edited.
  const [draft, setDraft] = useState<QuietHours | null>(null);
  const shown = draft ?? window ?? DEFAULT_QUIET_HOURS;
  const same = shown.starts === shown.ends;

  function edit(next: QuietHours) {
    setDraft(next);
    if (next.starts !== next.ends && next.starts && next.ends) {
      onChange(next, () => {
        setDraft(null);
      });
    }
  }

  // A stored time off the half-hour grid still shows as itself.
  const options = (value: string) =>
    HALF_HOURS.includes(value) ? HALF_HOURS : [...HALF_HOURS, value].sort();

  return (
    <SettingsPanel labelledBy="notif-quiet">
      <div className="flex items-start justify-between gap-4">
        <div className="flex min-w-0 flex-col gap-0.5">
          <h2 id="notif-quiet" className="text-foreground text-sm font-medium">
            Quiet hours
          </h2>
          {window ? (
            <p className="text-muted-foreground text-xs">
              No alerts or emails from {window.starts} to {window.ends}.
              Critical notifications still reach you, and email held during
              quiet hours arrives when they end.
            </p>
          ) : null}
        </div>
        <Switch
          label="Quiet hours"
          checked={window !== null}
          disabled={saving}
          onChange={(on) => {
            setDraft(null);
            onChange(on ? DEFAULT_QUIET_HOURS : null, () => undefined);
          }}
        />
      </div>
      {window ? (
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          {(['starts', 'ends'] as const).map((end) => {
            const label = end === 'starts' ? 'From' : 'To';
            return (
              <div
                key={end}
                className="text-foreground flex items-center gap-2 text-sm"
              >
                <span aria-hidden="true">{label}</span>
                {/* The app's own select (not a native one), so the open list
                  looks like every other menu here. */}
                <Select
                  value={shown[end]}
                  disabled={saving}
                  onValueChange={(next) => {
                    if (next) edit({ ...shown, [end]: next });
                  }}
                >
                  <SelectTrigger
                    aria-label={label}
                    className="h-8 w-auto gap-1.5 px-2 font-mono tabular-nums"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="max-h-72 overflow-y-auto">
                    {options(shown[end]).map((time) => (
                      <SelectItem
                        key={time}
                        value={time}
                        className="font-mono tabular-nums"
                      >
                        {time}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            );
          })}
          <span className="text-muted-foreground text-xs">
            {SETTINGS_COPY.zone(zone)}
          </span>
          {same ? (
            <p role="alert" className="text-destructive w-full text-xs">
              The start and end must differ.
            </p>
          ) : null}
        </div>
      ) : null}
    </SettingsPanel>
  );
}
